export const OCR_API_URL =
  process.env.REACT_APP_API_URL ||
  process.env.VITE_API_URL ||
  "https://checkinpro-ocr.web.id";

/** Base URL pipeline. Surat kesehatan dan foto profil memakai Ornith di server ini. */
export const OCR_PIPELINE_URL = `${OCR_API_URL.replace(/\/$/, "")}/api/v1/pipeline`;

/** ID kanonik sesuai API_SPEC — server menerima alias (case-insensitive). */
export const OCR_DOCUMENT_TYPES = {
  ktp: "ktp",
  npwp: "npwp",
  kk: "kk",
  rekening: "rekening",
  mutasi: "mutasi",
  skck: "skck",
  bpjs: "bpjs",
  bpjs_tk: "bpjs_tk",
  bpjs_kesanggupan: "bpjs_kesanggupan",
  jkn: "jkn",
  iuran: "iuran",
  vaksinasi_1: "vaksinasi_1",
  vaksinasi_2: "vaksinasi_2",
  vaksinasi_3: "vaksinasi_3",
  ijasah: "ijasah",
  transkrip: "transkrip",
  formulir_okb: "formulir_okb",
  formulir_lamaran: "formulir_lamaran",
  surat_lamaran: "surat_lamaran",
  pemadanan_npwp: "pemadanan_npwp",
  keterangan_kesehatan: "keterangan_kesehatan",
  foto_profile: "foto_profile",
  cv: "cv",
};

/**
 * Semua profil API_SPEC diverifikasi via POST /api/v1/pipeline sebelum upload,
 * kecuali profil di OCR_SKIP_VALIDATION_TYPES.
 * Jika layanan OCR tidak tersedia, upload tetap dilanjutkan tanpa *_validate_by_ocr.
 * Jika OCR merespons dan dokumen tidak lolos, upload dihentikan.
 */
export const OCR_SKIP_VALIDATION_TYPES = [
  "formulir_lamaran",
  "formulir_okb",
  "transkrip",
];

export const OCR_ENABLED_TYPES = Object.values(OCR_DOCUMENT_TYPES).filter(
  (type) => !OCR_SKIP_VALIDATION_TYPES.includes(type),
);

/** @deprecated Gunakan isOcrEnabledFor(documentType) */
export const OCR_ENABLED = OCR_ENABLED_TYPES.length > 0;

export function isOcrEnabledFor(documentType) {
  const id = normalizeDocumentType(documentType);
  if (OCR_SKIP_VALIDATION_TYPES.includes(id)) return false;
  return OCR_ENABLED_TYPES.includes(id);
}

/** Flag trial per karyawan dari halaman /administrator (`trial`). Default: mati. */
export function isEmployeeOcrEnabled(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

/**
 * Nama field Firestore/state untuk flag hasil OCR, dari field URL dokumen.
 * Contoh: foto_url → foto_validate_by_ocr
 */
export function getValidateByOcrFieldName(urlField) {
  if (typeof urlField !== "string" || !urlField.trim()) return null;
  if (urlField.endsWith("_url")) {
    return urlField.replace(/_url$/, "_validate_by_ocr");
  }
  return `${urlField}_validate_by_ocr`;
}

export const OCR_DOCUMENT_LABELS = {
  ktp: "KTP",
  npwp: "NPWP",
  kk: "Kartu Keluarga",
  rekening: "Rekening Tabungan",
  mutasi: "Mutasi / E-Statement",
  skck: "SKCK",
  bpjs: "BPJS Kesehatan",
  bpjs_tk: "BPJS Ketenagakerjaan",
  bpjs_kesanggupan: "Kesanggupan BPJS Kesehatan",
  jkn: "JKN Info Peserta",
  iuran: "Iuran JKN",
  vaksinasi_1: "Vaksinasi Dosis 1",
  vaksinasi_2: "Vaksinasi Dosis 2",
  vaksinasi_3: "Vaksinasi Dosis 3",
  ijasah: "Ijazah",
  transkrip: "Transkrip Nilai",
  formulir_okb: "Formulir OKB",
  formulir_lamaran: "Formulir Lamaran Pekerjaan",
  surat_lamaran: "Surat Lamaran",
  pemadanan_npwp: "Pemadanan NPWP",
  keterangan_kesehatan: "Surat Keterangan Kesehatan",
  foto_profile: "Foto Profil",
  cv: "CV",
};

export function normalizeDocumentType(documentType) {
  return typeof documentType === "string"
    ? documentType.trim().toLowerCase()
    : "";
}

export function isImageValidationProfile(documentType) {
  return (
    normalizeDocumentType(documentType) === OCR_DOCUMENT_TYPES.foto_profile
  );
}

export function isCvProfile(documentType) {
  return normalizeDocumentType(documentType) === OCR_DOCUMENT_TYPES.cv;
}

/** Surat kesehatan selalu Ornith — query ocr_mode diabaikan server. */
export function isHealthLetterProfile(documentType) {
  return (
    normalizeDocumentType(documentType) ===
    OCR_DOCUMENT_TYPES.keterangan_kesehatan
  );
}

/** Profil yang tidak mengirim expected_name. */
export function shouldSendExpectedName(documentType, userName) {
  const id = normalizeDocumentType(documentType);
  if (id === "foto_profile" || id === "mutasi") return false;
  return getExpectedUserName(userName) !== "";
}

export function normalizeExpectedBank(bank) {
  const id = normalizeDocumentType(bank);
  if (id === "mandiri" || id === "mas") return id;
  return "";
}

export function normalizeExpectedAccount(account) {
  const digits = String(account || "").replace(/\D/g, "");
  if (digits.length >= 10 && digits.length <= 13) return digits;
  return "";
}

export function getExpectedUserName(nameFromInput) {
  return typeof nameFromInput === "string" ? nameFromInput.trim() : "";
}

export function normalizeExpectedNik(nik) {
  const digits = String(nik || "").replace(/\D/g, "");
  return digits.length === 16 ? digits : "";
}

function getPipelineFileName(file) {
  const original = (file && file.name) || "document";
  const match = original.match(/(\.[a-zA-Z0-9]+)$/);
  const ext = match ? match[1].toLowerCase() : "";
  return `document${ext || ".jpg"}`;
}

/** Error infrastruktur OCR (HTTP 5xx / jaringan) — upload tetap jalan, tanpa pesan teknis. */
export function isOcrInfrastructureError(message, errorCode) {
  const code = typeof errorCode === "string" ? errorCode : "";
  if (/^pipeline\.(internal|ocr_unavailable|.+_unavailable)$/i.test(code)) {
    return true;
  }
  if (!message || typeof message !== "string") return false;
  return /server error|failed to fetch|network|timeout|abort|terhubung|econnrefused|load failed|ocr_unavailable|health_ai_unavailable|layanan ocr sedang tidak tersedia|tidak merespons|terjadi kesalahan sistem/i.test(
    message,
  );
}

function readErrorCases(source) {
  if (!source || !Array.isArray(source.error_cases)) return [];
  return source.error_cases
    .map((item) => (item && item.message ? String(item.message).trim() : ""))
    .filter(Boolean);
}

function firstBoolean(...values) {
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] === true) return true;
    if (values[i] === false) return false;
  }
  return null;
}

/** document_matched/valid bisa di root, validation, atau verdict. */
export function isPipelineDocumentMatched(result) {
  if (!result || typeof result !== "object") return false;
  const validation = result.validation || {};
  const verdict = result.verdict || {};
  return (
    firstBoolean(
      result.document_matched,
      result.valid,
      validation.document_matched,
      validation.valid,
      verdict.document_matched,
    ) === true
  );
}

/**
 * Kalimat UI dari respons service (API_SPEC).
 * HTTP 200 gagal: error_message, plus error_cases lain bila lebih dari satu.
 * Jangan pakai verdict.summary / explanation.summary.
 */
export function getUserFacingRejectionMessage(result) {
  if (!result) return "Gagal memproses dokumen.";
  if (isOcrDocumentAccepted(result)) return null;

  const validation = result.validation || {};
  const primary =
    (typeof result.error_message === "string" && result.error_message.trim()) ||
    (typeof validation.error_message === "string" &&
      validation.error_message.trim()) ||
    "";

  const cases = readErrorCases(result);
  const nestedCases = cases.length > 0 ? cases : readErrorCases(validation);
  const extra = nestedCases.filter((message) => message && message !== primary);

  const lines = [primary, ...extra].filter(Boolean);
  if (lines.length > 0) return lines.join("\n");
  return "Dokumen tidak lolos verifikasi.";
}

function buildVerifyOutcome(
  data,
  source,
  expectedName = "",
  expectedAccount = "",
  documentType = "",
  expectedBank = "",
) {
  const ok = isOcrDocumentAccepted(
    data,
    expectedName,
    expectedAccount,
    documentType,
    expectedBank,
  );
  return {
    ok,
    skipped: false,
    data,
    source,
    summary: getOcrVerdictSummary(
      data,
      expectedName,
      expectedAccount,
      documentType,
      expectedBank,
    ),
    rejectionMessage: ok
      ? null
      : getUserFacingRejectionMessage(
          data,
          expectedName,
          expectedAccount,
          documentType,
          expectedBank,
        ),
    error: null,
  };
}

function buildSkippedOutcome() {
  return {
    ok: false,
    skipped: true,
    data: null,
    source: "skipped",
    summary: null,
    rejectionMessage: null,
    error: null,
  };
}

/**
 * Satu request pipeline. Fallback AI (LLM) dijalankan server bila Paddle gagal.
 * Jika layanan tidak terjangkau, caller mengizinkan upload tanpa OCR.
 */
export async function verifyDocumentWithFallback(
  file,
  documentType,
  userName,
  options = {},
) {
  try {
    const data = await verifyDocument(file, documentType, userName, options);
    return buildVerifyOutcome(
      data,
      "pipeline",
      userName,
      options.expectedAccount || "",
      documentType,
      options.expectedBank || "",
    );
  } catch (err) {
    const message = (err && err.message) || "";
    const errorCode = (err && err.errorCode) || null;
    if (isOcrInfrastructureError(message, errorCode) || !message) {
      return buildSkippedOutcome();
    }
    return {
      ok: false,
      skipped: false,
      data: null,
      source: "pipeline",
      summary: null,
      rejectionMessage: message || "Gagal memproses dokumen.",
      error: message || null,
      errorCode,
    };
  }
}

export function parseOcrApiError(body, statusText) {
  if (!body) {
    return { message: statusText || "Gagal memproses dokumen.", errorCode: null };
  }

  const detail = body.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (item && (item.msg || item.message) ? String(item.msg || item.message).trim() : ""))
      .filter(Boolean);
    return {
      message: messages.join("\n") || statusText || "Gagal memproses dokumen.",
      errorCode: null,
    };
  }

  if (detail && typeof detail === "object") {
    const formatted = getUserFacingRejectionMessage({
      document_matched: false,
      error_message: detail.error_message,
      error_cases: detail.error_cases,
    });
    return {
      message: formatted || statusText || "Gagal memproses dokumen.",
      errorCode: detail.error_code || null,
    };
  }

  if (typeof detail === "string" && detail.trim()) {
    return { message: detail.trim(), errorCode: null };
  }

  const formatted = getUserFacingRejectionMessage(body);
  if (formatted && !isPipelineDocumentMatched(body)) {
    return { message: formatted, errorCode: body.error_code || null };
  }

  if (typeof body.message === "string" && body.message.trim()) {
    return { message: body.message.trim(), errorCode: body.error_code || null };
  }

  return { message: statusText || "Gagal memproses dokumen.", errorCode: null };
}

export function getPreprocessedImageDataUrl(result) {
  if (!result) return null;
  const profileId =
    result.document_profile_id ||
    (result.validation && result.validation.document_profile_id);
  if (isImageValidationProfile(profileId)) return null;
  const img = result.preprocessed_image;
  if (!img || !img.image_base64) return null;
  const mime = img.mime || "image/png";
  return `data:${mime};base64,${img.image_base64}`;
}

/**
 * Verifikasi dokumen via POST /api/v1/pipeline.
 * foto_profile dan keterangan_kesehatan diproses Ornith di server; query ocr_mode diabaikan.
 */
export async function verifyDocument(
  file,
  documentType,
  userName,
  options = {},
) {
  const profileId = normalizeDocumentType(documentType) || documentType;

  const {
    ocrMode = "fast",
    ppOcrTier = "medium",
    includePreprocessedImage = false,
    cvEducationQuery = "",
    cvExperienceQuery = "",
    expectedBank = "",
    expectedAccount = "",
    jobPosition = "",
    expectedNik = "",
  } = options;

  const imageOnly = isImageValidationProfile(profileId);
  const healthLetter = isHealthLetterProfile(profileId);
  const cvOnly = isCvProfile(profileId);
  const skipOcrQuery = imageOnly || healthLetter;

  const form = new FormData();
  form.append("file", file, getPipelineFileName(file));
  form.append("document_type", profileId);
  if (shouldSendExpectedName(profileId, userName)) {
    form.append("expected_name", getExpectedUserName(userName));
  }
  const nik = normalizeExpectedNik(expectedNik);
  if (nik && profileId !== "foto_profile" && profileId !== "mutasi") {
    form.append("expected_nik", nik);
  }
  const bank = normalizeExpectedBank(expectedBank);
  const account = normalizeExpectedAccount(expectedAccount);
  if ((profileId === "rekening" || profileId === "mutasi") && bank) {
    form.append("expected_bank", bank);
  }
  if ((profileId === "rekening" || profileId === "mutasi") && account) {
    form.append("expected_account", account);
  }
  if (profileId === "ktp" && jobPosition) {
    form.append("job_position", String(jobPosition).trim());
  }

  const params = new URLSearchParams();
  params.set("skip_passthrough", "true");
  if (cvOnly) {
    const education = getExpectedUserName(cvEducationQuery);
    const experience = getExpectedUserName(cvExperienceQuery);
    const name = getExpectedUserName(userName);
    if (name) params.set("cv_search_query", name);
    if (education) params.set("cv_education_query", education);
    if (experience) params.set("cv_experience_query", experience);
  } else if (!skipOcrQuery) {
    params.set("ocr_mode", ocrMode);
    if (ocrMode === "fast") {
      params.set("pp_ocr_tier", ppOcrTier);
    }
    params.set(
      "include_preprocessed_image",
      String(includePreprocessedImage),
    );
  }

  const query = params.toString();
  const url = query ? `${OCR_PIPELINE_URL}?${query}` : OCR_PIPELINE_URL;

  const timeoutMs = cvOnly || healthLetter ? 180000 : 120000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      body: form,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      throw new Error(
        cvOnly
          ? "Proses CV timeout (>3 menit). Coba lagi dengan file yang lebih kecil."
          : healthLetter
            ? "Verifikasi surat kesehatan timeout (>3 menit). Coba lagi dengan gambar yang lebih kecil."
            : "Verifikasi dokumen timeout (>2 menit). Coba lagi dengan gambar yang lebih kecil.",
      );
    }
    throw new Error(
      "Server error",
    );
  }
  clearTimeout(timeoutId);

  const body = await res.json().catch(() => ({}));

  if (!res.ok) {
    const parsed = parseOcrApiError(body, res.statusText);
    const error = new Error(parsed.message);
    error.errorCode = parsed.errorCode;
    throw error;
  }

  return body;
}

export function isOcrDocumentAccepted(result) {
  if (!result || result.success === false) return false;
  return isPipelineDocumentMatched(result);
}

export function getOcrVerdictSummary(
  result,
  expectedName = "",
  expectedAccount = "",
  documentType = "",
  expectedBank = "",
) {
  if (!result) return "Verifikasi selesai.";
  if (
    isOcrDocumentAccepted(
      result,
      expectedName,
      expectedAccount,
      documentType,
      expectedBank,
    )
  ) {
    return "Dokumen valid.";
  }
  return getUserFacingRejectionMessage(
    result,
    expectedName,
    expectedAccount,
    documentType,
    expectedBank,
  );
}

export function getOcrRejectionMessage(
  result,
  expectedName = "",
  expectedAccount = "",
  documentType = "",
  expectedBank = "",
) {
  return getUserFacingRejectionMessage(
    result,
    expectedName,
    expectedAccount,
    documentType,
    expectedBank,
  );
}
